import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  IconButton,
  Switch,
  Text,
} from "@radix-ui/themes";
import { Plus, Trash2 } from "lucide-react";
import { Form, Formik } from "formik";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CheckboxField from "@/components/form/CheckboxField";
import SelectField from "@/components/form/SelectField";
import SideDrawer from "@/components/ui/SideDrawer";
import { BasicTable, Column } from "@/components/ui/BasicTable";
import withTenantAdminRequired from "@/hoc/withTenantAdminRequired";
import { anyService } from "@/services/breeze/AnyService";
import { groupService } from "@/services/breeze/GroupService";
import { RlsGroup, UpdateRlsGroup } from "@/types/breeze/Any";
import { Group } from "@/types/breeze/Group";

interface EntityRlsGroupsProps {
  entityName: string;
  entityId: number;
}

const EntityRlsGroups = ({ entityName, entityId }: EntityRlsGroupsProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: groups, isLoading: isLoadingGroups } = useQuery<Group[]>({
    queryKey: ["groups"],
    queryFn: () => groupService.listGroups(),
  });

  const { data: rlsGroups = [], isLoading } = useQuery<RlsGroup[]>({
    queryKey: ["rls-groups", entityName, entityId],
    queryFn: () => anyService.getRlsGroups(entityName, entityId),
  });

  const updateRlsGroupMutation = useMutation({
    mutationFn: (grant: UpdateRlsGroup) =>
      anyService.updateRlsGroup(entityName, entityId, grant),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["rls-groups", entityName, entityId],
      });
      queryClient.invalidateQueries({
        queryKey: ["entity-data", entityName, entityId],
      });
      setIsDrawerOpen(false);
    },
  });

  const deleteRlsGroupMutation = useMutation({
    mutationFn: (rlsGroupId: number) =>
      anyService.deleteRlsGroup(entityName, entityId, rlsGroupId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["rls-groups", entityName, entityId],
      });
      queryClient.invalidateQueries({
        queryKey: ["entity-data", entityName, entityId],
      });
    },
  });

  const availableGroups = useMemo(
    () =>
      groups?.filter(
        (g) => !rlsGroups.some((rls) => rls.group_id === g.id),
      ) ?? [],
    [groups, rlsGroups],
  );

  const columns: Column<RlsGroup>[] = [
    {
      header: "Group",
      accessor: "group_name",
      cellRenderer: (row) => row.group_name,
    },
    {
      header: "Read Only",
      accessor: "readonly",
      cellRenderer: (row) => (
        <Switch
          checked={row.readonly}
          onCheckedChange={(value) =>
            updateRlsGroupMutation.mutate({
              group_id: row.group_id,
              readonly: value,
            })
          }
          size="2"
        />
      ),
    },
    {
      header: "Actions",
      accessor: "id",
      cellRenderer: (row) => (
        <IconButton
          size="1"
          variant="ghost"
          color="red"
          onClick={() => {
            if (confirm("Are you sure you want to remove this group's access?")) {
              deleteRlsGroupMutation.mutate(row.id);
            }
          }}
          title="Remove group"
        >
          <Trash2 size={16} />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Flex justify="end" mb="4">
        <Button
          onClick={() => setIsDrawerOpen(true)}
          disabled={availableGroups.length === 0}
          size="2"
        >
          <Plus size={16} />
          Add Group Access
        </Button>
      </Flex>

      {!isLoading && rlsGroups.length === 0 ? (
        <Box py="8">
          <Text align="center" color="gray" as="p">
            No groups have been granted access to this record
          </Text>
        </Box>
      ) : (
        <BasicTable columns={columns} data={rlsGroups} isLoading={isLoading} />
      )}

      <SideDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="Add Group Access"
      >
        <Box p="4">
          {availableGroups.length === 0 ? (
            <Box py="8">
              <Text align="center" color="gray" as="p">
                All groups have already been granted access, or no groups exist yet
              </Text>
            </Box>
          ) : (
            <Formik
              initialValues={{ group_id: 0, readonly: true }}
              onSubmit={(values) =>
                updateRlsGroupMutation.mutate({
                  group_id: values.group_id,
                  readonly: values.readonly,
                })
              }
            >
              {({ values }) => (
                <Form>
                  <Flex direction="column" gap="4">
                    <SelectField
                      name="group_id"
                      label="Group"
                      required
                      type="number"
                      disabled={isLoadingGroups}
                    >
                      {availableGroups.map((g) => (
                        <SelectField.Item key={g.id} value={g.id}>
                          {g.name}
                        </SelectField.Item>
                      ))}
                    </SelectField>

                    <CheckboxField name="readonly" label="Read Only Access" />

                    <Grid columns="2" gap="4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsDrawerOpen(false)}
                        style={{ width: "100%" }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={
                          !values.group_id || updateRlsGroupMutation.isPending
                        }
                        style={{ width: "100%" }}
                      >
                        {updateRlsGroupMutation.isPending
                          ? "Adding..."
                          : "Add Group"}
                      </Button>
                    </Grid>
                  </Flex>
                </Form>
              )}
            </Formik>
          )}
        </Box>
      </SideDrawer>
    </Box>
  );
};

export default withTenantAdminRequired(EntityRlsGroups);
